import type {
    Filter,
    PinnedSavedFilterTab,
    PinnedSavedFiltersPreferenceValue,
    SavedFilterPreferenceValue
} from '../../common/types/savedFilters';

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object';
}

/**
 * Parses Saved Filter preference value. Supports legacy Filter[] and wrapped objects.
 * Legacy pinAsTab / pinnedAt are kept for migration fallback only.
 */
export function parseSavedFilterPreferenceValue(value: unknown): SavedFilterPreferenceValue | null {
    if (typeof value === 'string') {
        try {
            return parseSavedFilterPreferenceValue(JSON.parse(value));
        } catch {
            return null;
        }
    }

    if (Array.isArray(value)) {
        const filters: Filter[] = value;
        return { filters };
    }

    if (isRecord(value) && Array.isArray(value.filters)) {
        const filters: Filter[] = value.filters;
        return {
            filters,
            pinAsTab: Boolean(value.pinAsTab),
            pinnedAt: typeof value.pinnedAt === 'string' ? value.pinnedAt : undefined
        };
    }

    return null;
}

/** Serializes Saved Filter preference value as filters only (pins are personal). */
export function serializeSavedFilterPreferenceValue(payload: Pick<SavedFilterPreferenceValue, 'filters'>): string {
    return JSON.stringify({ filters: payload.filters });
}

export function parsePinnedSavedFiltersPreferenceValue(value: unknown): PinnedSavedFiltersPreferenceValue | null {
    if (typeof value === 'string') {
        try {
            return parsePinnedSavedFiltersPreferenceValue(JSON.parse(value));
        } catch {
            return null;
        }
    }

    if (!isRecord(value) || !Array.isArray(value.pinnedTabs)) {
        return null;
    }

    const pinnedTabs: PinnedSavedFilterTab[] = [];
    for (const entry of value.pinnedTabs) {
        if (!isRecord(entry) || typeof entry.preferenceId !== 'string' || !entry.preferenceId) continue;
        pinnedTabs.push({
            preferenceId: entry.preferenceId,
            pinnedAt: typeof entry.pinnedAt === 'string' ? entry.pinnedAt : new Date(0).toISOString()
        });
    }

    return {
        schemaVersion: 1,
        manualOrder: Boolean(value.manualOrder),
        pinnedTabs
    };
}

export function serializePinnedSavedFiltersPreferenceValue(payload: PinnedSavedFiltersPreferenceValue): string {
    return JSON.stringify({
        schemaVersion: 1,
        ...(payload.manualOrder ? { manualOrder: true } : {}),
        pinnedTabs: payload.pinnedTabs
    });
}

/** Adds or removes a pin entry in the personal pinned-tabs preference. */
export function upsertPinnedTab(
    current: PinnedSavedFiltersPreferenceValue | null | undefined,
    preferenceId: string,
    pinAsTab: boolean,
    previousPinnedAt?: string
): PinnedSavedFiltersPreferenceValue {
    const without = (current?.pinnedTabs ?? []).filter((tab) => tab.preferenceId !== preferenceId);
    const manualOrder = Boolean(current?.manualOrder);

    if (!pinAsTab) {
        const nextPinnedValue: PinnedSavedFiltersPreferenceValue = {
            schemaVersion: 1,
            ...(manualOrder ? { manualOrder: true } : {}),
            pinnedTabs: without
        };
        return nextPinnedValue;
    }

    const nextPinnedValue: PinnedSavedFiltersPreferenceValue = {
        schemaVersion: 1,
        ...(manualOrder ? { manualOrder: true } : {}),
        pinnedTabs: [...without, { preferenceId, pinnedAt: previousPinnedAt || new Date().toISOString() }]
    };

    return nextPinnedValue;
}

/**
 * Rewrites pinned tab order to match `orderedPreferenceIds` and marks the preference as manually ordered.
 * Unknown ids in the ordered list are skipped; pinned entries missing from the list are appended in prior order.
 */
export function reorderPinnedTabs(
    current: PinnedSavedFiltersPreferenceValue | null | undefined,
    orderedPreferenceIds: string[]
): PinnedSavedFiltersPreferenceValue {
    const existingById = new Map((current?.pinnedTabs ?? []).map((tab) => [tab.preferenceId, tab]));
    const seen = new Set<string>();
    const pinnedTabs: PinnedSavedFilterTab[] = [];

    for (const preferenceId of orderedPreferenceIds) {
        const existing = existingById.get(preferenceId);
        if (!existing || seen.has(preferenceId)) continue;
        pinnedTabs.push(existing);
        seen.add(preferenceId);
    }

    for (const tab of current?.pinnedTabs ?? []) {
        if (seen.has(tab.preferenceId)) continue;
        pinnedTabs.push(tab);
        seen.add(tab.preferenceId);
    }

    const nextPinnedValue: PinnedSavedFiltersPreferenceValue = {
        schemaVersion: 1,
        manualOrder: true,
        pinnedTabs
    };

    return nextPinnedValue;
}

export function parsePinnedAtTimestamp(pinnedAt?: string): number {
    if (!pinnedAt) return 0;
    const time = Date.parse(pinnedAt);
    return Number.isNaN(time) ? 0 : time;
}
